import { Dispute } from 'APIs/disputes/disputes.types';
import Spinner from 'Components/Spinner/Spinner';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import DataTable, { TableColumn, createTheme } from 'react-data-table-component';
import { FaRegEdit } from 'react-icons/fa';
import { Currency, toCurrency } from 'utils/currency';

import { TableStyleSettings, TableThemeSettings } from './Constants';
import EmptyComponent from './EmptyComponent';
import ErrorComponent from './ErrorComponent';
import { ActionButton, DisputeDataTableContainer, DisputeLabel, MessageWrapper } from './styles';
import { disputeOutcomes } from './types';

createTheme('polarized', TableThemeSettings);

interface Props {
  disputeList?: Array<Dispute>;
  totalRows: number;
  limit: number;
  activePage: number;
  fetchListLoader: boolean;
  isErrorRequest?: boolean;
  handlePerRowsChange: (newPerPage: number, page: number) => void;
  handlePageChange: (page: number) => void;
  setDetailsDispute: (dispute: Dispute) => void;
  setModalDispute: (dispute: Dispute) => void;
}

function DisputeDataTable({
  disputeList,
  totalRows,
  limit,
  activePage,
  fetchListLoader,
  isErrorRequest,
  handlePageChange,
  handlePerRowsChange,
  setDetailsDispute,
  setModalDispute,
}: Props) {
  const EditIcon = FaRegEdit as React.FunctionComponent;

  const columns: TableColumn<Dispute>[] = useMemo(() => {
    return [
      {
        name: 'Dispute ID',
        cell: (row: Dispute) => row.disputeId,
        minWidth: '80px',
        width: '200px',
        className: 'dispute-id-column',
      },
      {
        name: 'Amount',
        selector: (row: Dispute) => toCurrency(row.disputedAmount, row.currency as Currency),
        width: '110px',
        right: true,
      },
      {
        name: '',
        selector: (row: Dispute) => row.currency as Currency,
        width: '60px',
        left: true,
      },
      {
        name: 'Outcome',
        cell: (row: Dispute) =>
          row.outcome ? (
            <DisputeLabel className={disputeOutcomes[row.outcome].className}>
              {disputeOutcomes[row.outcome].label}
            </DisputeLabel>
          ) : (
            ''
          ),
        width: '100px',
        left: true,
      },
      {
        name: 'Reason',
        selector: (row: Dispute) => row.reason,
        width: '130px',
        left: true,
      },
      {
        name: 'State',
        selector: (row: Dispute) => row.state,
        width: '80px',
        left: true,
      },
      {
        name: 'Customer email',
        selector: (row: Dispute) => row.customerEmailAddress,
        width: '250px',
        left: true,
      },
      {
        name: 'Updated at',
        selector: (row: Dispute) => `${DateTime.fromISO(row.updatedAt).toFormat('dd/LL/yyyy, HH:mm')}`,
        minWidth: '80px',
      },
      {
        name: '',
        cell: (row: Dispute) =>
          row.outcome === 'pending' && (
            <ActionButton
              className="hover-button"
              title="Update outcome"
              onClick={(e) => {
                e.preventDefault();
                setModalDispute(row);
              }}
            >
              <EditIcon />
            </ActionButton>
          ),
        width: '100px',
        center: true,
      },
    ];
  }, [EditIcon, setModalDispute]);

  return (
    <DisputeDataTableContainer>
      <DataTable
        pagination
        responsive
        columns={columns}
        onRowClicked={(row, e) => {
          e.preventDefault();
          setDetailsDispute(row);
        }}
        data={disputeList ?? []}
        customStyles={TableStyleSettings}
        theme="polarized"
        className=""
        progressPending={fetchListLoader}
        progressComponent={
          <MessageWrapper>
            <Spinner color="#0016D1" height="25px" width="25px" />
          </MessageWrapper>
        }
        noDataComponent={
          isErrorRequest ? (
            <MessageWrapper>
              <ErrorComponent />
            </MessageWrapper>
          ) : (
            <MessageWrapper>
              <EmptyComponent />
            </MessageWrapper>
          )
        }
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationPerPage={limit}
        paginationDefaultPage={activePage}
        sortServer
        persistTableHead
        defaultSortFieldId={4}
        defaultSortAsc={false}
        paginationRowsPerPageOptions={[20, 40, 60, 80, 100]}
      />
    </DisputeDataTableContainer>
  );
}

export default DisputeDataTable;
